import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Group,
  Paper,
  Stack,
  Title,
  Text,
  Table,
  Badge,
  ActionIcon,
  Pagination,
  Tooltip,
  Modal,
  Divider,
  Code,
  Button,
  ThemeIcon,
  Loader,
} from "@mantine/core";
import coreApi from "../utils/api/coreApi";
import useApi from "../utils/api/useApi";
import { HealthCheck, HealthCheckRun } from "../utils/api/api_types";
import { HealthCheckRunStatusColors } from "../utils/enum_records";
import {
  IconFileInfo,
  IconBellOff,
  IconBell,
  IconActivityHeartbeat,
} from "@tabler/icons-react";
import { formatDate } from "../utils/dates";
import { MOBILE_VIEW_MAX_WIDTH_EM } from "../Settings";
import { useMediaQuery } from "@mantine/hooks";
import HealthCheckDescription from "./HealthCheckDescription";
import HealthCheckSchedule from "./HealthCheckSchedule";
import { notifications } from "@mantine/notifications";
import { toggleSubscribeHealthCheck } from "../utils/health_checks";
import { STANDARD_ERROR_MESSAGE } from "../utils/constants";
import { useGlobalContext } from "../context/globalContext";

const getRunDetailText = (detail: unknown) => JSON.stringify(detail, null, 2);

const getRunDetailModalSize = (detailText: string, isMobile: boolean) => {
  if (isMobile) return undefined;
  if (detailText.length > 12000) return 1440;
  if (detailText.length > 5000) return 1280;
  return 1100;
};

export default function HealthCheckDetailPage() {
  const { latestEvent } = useGlobalContext();

  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_VIEW_MAX_WIDTH_EM})`);

  const healthCheckDetailRes = useApi<HealthCheck | null>({
    apiFunc:
      id === undefined ? undefined : () => coreApi.healthChecksRetrieve(id),
    defaultData: null,
    paginated: false,
  });

  const [loadingSubscribe, setLoadingSubscribe] = useState(false);
  const handleToggleSubscribeHealthCheck = async () => {
    if (!id) return;

    try {
      setLoadingSubscribe(true);

      await toggleSubscribeHealthCheck(id);

      healthCheckDetailRes.refresh();
    } catch (e) {
      notifications.show({
        title: "Error occured!",
        message: STANDARD_ERROR_MESSAGE,
        color: "red",
      });
    } finally {
      setLoadingSubscribe(false);
    }
  };

  const healthChecRunsRes = useApi<HealthCheckRun[]>({
    apiFunc:
      id === undefined
        ? undefined
        : (data) => coreApi.healthChecksRuns(id, data),
    defaultData: [],
    paginated: true,
  });

  const healthChecRunsData = healthChecRunsRes.responseData.data;

  const helathCheckRunsRows = healthChecRunsData.map((healthCheckRun, i) => (
    <Table.Tr key={i}>
      <Table.Td>{formatDate(healthCheckRun.created_at)}</Table.Td>
      {!isMobile && (
        <Table.Td>{formatDate(healthCheckRun.completed_at)}</Table.Td>
      )}
      {!isMobile && (
        <Table.Td>
          {healthCheckRun?.triggered_by_user?.username || "🤖 AUTO"}
        </Table.Td>
      )}
      <Table.Td>
        <Badge
          color={HealthCheckRunStatusColors[healthCheckRun.status]}
          variant="light"
        >
          {healthCheckRun.status.replace(/_/g, " ")}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Tooltip label="View Detail">
          <ActionIcon
            variant="light"
            onClick={() => setSelectedHealthCheckRun(healthCheckRun)}
          >
            <IconFileInfo />
          </ActionIcon>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  ));

  const [selectedHealthCheckRun, setSelectedHealthCheckRun] =
    useState<HealthCheckRun | null>(null);
  const selectedHealthCheckRunDetailText =
    selectedHealthCheckRun === null
      ? ""
      : getRunDetailText(selectedHealthCheckRun.detail);

  useEffect(() => {
    if (searchParams.get("latestRun") !== "1") return;
    if (selectedHealthCheckRun !== null) return;
    if (healthChecRunsData.length === 0) return;

    setSelectedHealthCheckRun(healthChecRunsData[0]);
  }, [searchParams, selectedHealthCheckRun, healthChecRunsData]);

  const clearLatestRunParam = () => {
    if (searchParams.get("latestRun") !== "1") return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("latestRun");
    setSearchParams(nextSearchParams, { replace: true });
  };

  // If there was an update in the health check that is on the current page - refresh
  useEffect(() => {
    if (!latestEvent) return;

    const healthCheckId = latestEvent["health_check_id"];
    if (healthCheckId === undefined || healthCheckId.toString() != id) return;

    healthCheckDetailRes.refresh();

    // We are on first page, might as well refresh
    if (healthChecRunsRes.responseData.metaData.page === 1)
      healthChecRunsRes.refresh();
  }, [JSON.stringify(latestEvent)]);

  const healthCheckDetailData = healthCheckDetailRes.responseData.data;
  if (healthCheckDetailData === null) return <></>;

  return (
    <>
      {selectedHealthCheckRun && (
        <Modal
          opened={selectedHealthCheckRun != null}
          centered
          size={getRunDetailModalSize(
            selectedHealthCheckRunDetailText,
            Boolean(isMobile)
          )}
          onClose={() => {
            setSelectedHealthCheckRun(null);
            clearLatestRunParam();
          }}
          title="Health Check Run Detail"
        >
          <Stack>
            <Group justify="space-between" align="flex-start">
              <div>
                <Text>
                  <b>Started:</b>{" "}
                  {formatDate(selectedHealthCheckRun.created_at)}
                </Text>
                <Text>
                  <b>Finished: </b>{" "}
                  {formatDate(selectedHealthCheckRun.completed_at)}
                </Text>
                <Text>
                  <b>Triggered By: </b>{" "}
                  {selectedHealthCheckRun?.triggered_by_user?.username ||
                    "🤖 AUTO"}
                </Text>
              </div>
              <Badge
                color={
                  HealthCheckRunStatusColors[selectedHealthCheckRun.status]
                }
                variant="light"
              >
                {selectedHealthCheckRun.status.replace(/_/g, " ")}
              </Badge>
            </Group>
            <Divider />
            <Code
              block
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: isMobile ? undefined : "70vh",
                overflow: "auto",
              }}
            >
              {selectedHealthCheckRunDetailText}
            </Code>
          </Stack>
        </Modal>
      )}

      {/* Main Content */}
      <Stack mt="sm">
        <Paper p="md">
          <Stack gap={5}>
            <Group justify="space-between">
              <Group>
                <ThemeIcon size="xl">
                  <IconActivityHeartbeat />
                </ThemeIcon>
                <Title order={4}>{healthCheckDetailData.name}</Title>
                <Button
                  loading={loadingSubscribe}
                  variant={
                    healthCheckDetailData.subscribed ? "outline" : "light"
                  }
                  leftSection={
                    healthCheckDetailData.subscribed ? (
                      <IconBell />
                    ) : (
                      <IconBellOff />
                    )
                  }
                  onClick={handleToggleSubscribeHealthCheck}
                  size={isMobile ? "sm" : "compact-sm"}
                  fullWidth={isMobile}
                >
                  {healthCheckDetailData.subscribed
                    ? "Subscribed"
                    : "Subscribe"}
                </Button>
              </Group>
              <Group>
                <Text>
                  {healthCheckDetailData.app.name.replace(/_/g, " ")} (
                  {healthCheckDetailData.method_name})
                </Text>
              </Group>
            </Group>

            <Text>
              <b>Created:</b> {formatDate(healthCheckDetailData.created_at)}
            </Text>

            <HealthCheckSchedule
              healthCheck={healthCheckDetailData}
              onScheduleUpdated={() => {
                healthCheckDetailRes.refresh();
              }}
              onManualTrigger={() => {
                healthChecRunsRes.refresh();
              }}
            />

            <HealthCheckDescription
              healthCheck={healthCheckDetailData}
              onDescriptionUpdated={() => {
                healthCheckDetailRes.refresh();
              }}
            />
          </Stack>
        </Paper>

        <Title order={2}>Latest Runs</Title>

        {healthChecRunsRes.loading ? (
          <Stack
            align="center"
            justify="center"
            style={{ height: "calc(100vh - 210px)" }}
          >
            <Loader type="bars" />
          </Stack>
        ) : (
          <Stack align="center">
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Started</Table.Th>
                  {!isMobile && <Table.Th>Finished</Table.Th>}
                  {!isMobile && <Table.Th>Triggered By</Table.Th>}
                  <Table.Th>Status</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{helathCheckRunsRows}</Table.Tbody>
            </Table>
            {healthChecRunsRes.responseData.metaData &&
            healthChecRunsRes.responseData.metaData.total_pages > 1 ? (
              <Pagination
                mb={30}
                py="xl"
                value={healthChecRunsRes.responseData.metaData.page}
                onChange={(p) => healthChecRunsRes.setParams({ page: p })}
                total={healthChecRunsRes.responseData.metaData.total_pages}
              />
            ) : (
              <></>
            )}
          </Stack>
        )}
      </Stack>
    </>
  );
}
